import { useState, useMemo } from "react";
import React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Pencil, Trash2, Heart, Calendar, Activity, Thermometer, Wind, Droplet } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronUp } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface VitalSign {
  type: "vital_sign";
  date?: string;
  vital_type: string;
  value: string | number;
  unit?: string;
  classification?: string;
  time?: string;
}

interface SmartfileSession {
  id: string;
  file_name: string;
  content_type: string | null;
  ai_summary?: string | null;
  created_at: string;
  structured_records?: any[] | null;
  patient_id: string | null;
  status: string | null;
}

interface SmartfileVitalsDetailsProps {
  session: SmartfileSession;
  onRefresh: () => void;
  onRecordsChange?: (updatedRecords: any[]) => void;
  expandAllByDefault?: boolean;
}

interface EditableVitalSign extends VitalSign {
  _index?: number;
}

// Mapeamento de tipos de sinais vitais para labels
const vitalTypeLabels: Record<string, string> = {
  pressao_arterial: 'Pressão Arterial',
  blood_pressure: 'Pressão Arterial',
  pa: 'Pressão Arterial',
  frequencia_cardiaca: 'Frequência Cardíaca',
  fc: 'Frequência Cardíaca',
  heart_rate: 'Frequência Cardíaca',
  temperatura: 'Temperatura',
  temperature: 'Temperatura',
  temp: 'Temperatura',
  frequencia_respiratoria: 'Frequência Respiratória',
  fr: 'Frequência Respiratória',
  respiratory_rate: 'Frequência Respiratória',
  saturacao_oxigenio: 'Saturação de Oxigênio',
  spo2: 'Saturação de Oxigênio',
  oxygen_saturation: 'Saturação de Oxigênio',
  glicemia: 'Glicemia',
  glucose: 'Glicemia',
  blood_glucose: 'Glicemia',
};

// Mapeamento de tipos de sinais vitais para ícones
const vitalTypeIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  pressao_arterial: Heart,
  blood_pressure: Heart,
  pa: Heart,
  frequencia_cardiaca: Activity,
  fc: Activity,
  heart_rate: Activity,
  temperatura: Thermometer,
  temperature: Thermometer,
  temp: Thermometer,
  frequencia_respiratoria: Wind,
  fr: Wind,
  respiratory_rate: Wind,
  saturacao_oxigenio: Droplet,
  spo2: Droplet,
  oxygen_saturation: Droplet,
  glicemia: Activity,
  glucose: Activity,
  blood_glucose: Activity,
};

// Mapeamento de unidades padrão por tipo
const defaultUnits: Record<string, string> = {
  pressao_arterial: 'mmHg',
  blood_pressure: 'mmHg',
  pa: 'mmHg',
  frequencia_cardiaca: 'bpm',
  fc: 'bpm',
  heart_rate: 'bpm',
  temperatura: '°C',
  temperature: '°C',
  temp: '°C',
  frequencia_respiratoria: 'rpm',
  fr: 'rpm',
  respiratory_rate: 'rpm',
  saturacao_oxigenio: '%',
  spo2: '%',
  oxygen_saturation: '%',
  glicemia: 'mg/dL',
  glucose: 'mg/dL',
  blood_glucose: 'mg/dL',
};

export function SmartfileVitalsDetails({ session, onRefresh, onRecordsChange, expandAllByDefault = false }: SmartfileVitalsDetailsProps) {
  const isMobile = useIsMobile();

  // Filtrar apenas vital_sign - memoizado
  const vitalsRecords: EditableVitalSign[] = useMemo(() => {
    return (session.structured_records || [])
      .filter((record: any): record is VitalSign =>
        typeof record === 'object' &&
        record !== null &&
        'type' in record &&
        record.type === 'vital_sign'
      )
      .map((record: VitalSign, index: number) => ({
        ...record,
        _index: index
      }));
  }, [session.structured_records]);

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editedRecords, setEditedRecords] = useState<EditableVitalSign[]>(vitalsRecords);
  const [isSaving, setIsSaving] = useState(false);
  const [dateSortOrder, setDateSortOrder] = useState<'asc' | 'desc'>('desc');
  const [expandedDateGroups, setExpandedDateGroups] = useState<Map<string, boolean>>(new Map());

  // Atualizar editedRecords quando vitalsRecords mudar
  React.useEffect(() => {
    setEditedRecords(vitalsRecords);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.structured_records]);

  const handleSave = async (index: number) => {
    setIsSaving(true);
    try {
      const updatedRecords = [...(session.structured_records || [])];

      // Encontrar o índice real no array completo
      let vitalsCount = 0;
      let realIndex = -1;

      for (let i = 0; i < updatedRecords.length; i++) {
        const record = updatedRecords[i];
        if (typeof record === 'object' && record !== null && 'type' in record &&
          record.type === 'vital_sign') {
          if (vitalsCount === index) {
            realIndex = i;
            break;
          }
          vitalsCount++;
        }
      }

      if (realIndex !== -1) {
        updatedRecords[realIndex] = editedRecords[index];

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success("✅ Registro atualizado com sucesso!");
        setEditingIndex(null);
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      toast.error("❌ Erro ao salvar alterações");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (index: number) => {
    try {
      const updatedRecords = [...(session.structured_records || [])];

      let vitalsCount = 0;
      let realIndex = -1;

      for (let i = 0; i < updatedRecords.length; i++) {
        const record = updatedRecords[i];
        if (typeof record === 'object' && record !== null && 'type' in record &&
          record.type === 'vital_sign') {
          if (vitalsCount === index) {
            realIndex = i;
            break;
          }
          vitalsCount++;
        }
      }

      if (realIndex !== -1) {
        updatedRecords.splice(realIndex, 1);

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success("✅ Registro excluído com sucesso!");
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao excluir:', error);
      toast.error("❌ Erro ao excluir registro");
    }
  };

  const handleCancel = () => {
    setEditedRecords(vitalsRecords);
    setEditingIndex(null);
  };

  if (vitalsRecords.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground bg-background border-t">
        <p className="text-sm">Nenhum sinal vital encontrado nesta sessão</p>
      </div>
    );
  }

  // Agrupar por mês/ano
  const groupedByDate = vitalsRecords.reduce((acc, record, index) => {
    const date = record.date || session.created_at;
    const dateObj = new Date(date);

    // Validar se a data é válida
    if (isNaN(dateObj.getTime())) {
      // Se a data for inválida, usar uma chave padrão
      const invalidKey = 'invalid-date';
      if (!acc[invalidKey]) {
        acc[invalidKey] = {
          displayKey: 'Data Inválida',
          records: []
        };
      }
      acc[invalidKey].records.push({ ...record, _index: index });
      return acc;
    }

    const monthYearKey = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}`;
    const displayKey = `${String(dateObj.getMonth() + 1).padStart(2, '0')}/${dateObj.getFullYear()}`;

    if (!acc[monthYearKey]) {
      acc[monthYearKey] = {
        displayKey,
        records: []
      };
    }
    acc[monthYearKey].records.push({ ...record, _index: index });
    return acc;
  }, {} as Record<string, { displayKey: string; records: EditableVitalSign[] }>);

  // Ordenar por mês/ano conforme o estado de ordenação
  const sortedDates = Object.keys(groupedByDate).sort((a, b) => {
    const dateA = new Date(a + '-01').getTime();
    const dateB = new Date(b + '-01').getTime();
    return dateSortOrder === 'asc' ? dateA - dateB : dateB - dateA;
  });

  // Toggle de expansão de grupos de data
  const toggleDateGroup = (dateKey: string, isOpen: boolean) => {
    setExpandedDateGroups((prev) => {
      const next = new Map(prev);
      next.set(dateKey, isOpen);
      return next;
    });
  };

  // Toggle de ordenação de data
  const toggleDateSort = () => {
    setDateSortOrder((prev) => prev === 'asc' ? 'desc' : 'asc');
  };

  // Função para renderizar uma linha de registro
  const renderRecordRow = (record: EditableVitalSign, groupIndex: number, dateKey: string) => {
    const originalIndex = record._index ?? groupIndex;
    const isEditing = editingIndex === originalIndex;
    const Icon = vitalTypeIcons[record.vital_type?.toLowerCase()] || Heart;

    return (
      <React.Fragment key={`${dateKey}-${originalIndex}`}>
        {isEditing ? (
          <tr className="border-b border-border">
            <td colSpan={6} className="p-3 bg-muted/30">
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Data</Label>
                    <Input
                      type="date"
                      value={editedRecords[originalIndex]?.date || ''}
                      onChange={(e) => {
                        const updated = [...editedRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          date: e.target.value
                        };
                        setEditedRecords(updated);
                      }}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Tipo</Label>
                    <Select
                      value={editedRecords[originalIndex]?.vital_type || ''}
                      onValueChange={(value) => {
                        const updated = [...editedRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          vital_type: value,
                          unit: defaultUnits[value] || updated[originalIndex].unit
                        };
                        setEditedRecords(updated);
                      }}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue placeholder="Selecione o tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pressao_arterial">Pressão Arterial</SelectItem>
                        <SelectItem value="frequencia_cardiaca">Frequência Cardíaca</SelectItem>
                        <SelectItem value="temperatura">Temperatura</SelectItem>
                        <SelectItem value="frequencia_respiratoria">Frequência Respiratória</SelectItem>
                        <SelectItem value="saturacao_oxigenio">Saturação de Oxigênio</SelectItem>
                        <SelectItem value="glicemia">Glicemia</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Valor</Label>
                    <Input
                      value={editedRecords[originalIndex]?.value ?? ''}
                      onChange={(e) => {
                        const updated = [...editedRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          value: e.target.value
                        };
                        setEditedRecords(updated);
                      }}
                      placeholder="Ex: 120/80, 72, 36.5"
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Unidade</Label>
                    <Input
                      value={editedRecords[originalIndex]?.unit || ''}
                      onChange={(e) => {
                        const updated = [...editedRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          unit: e.target.value
                        };
                        setEditedRecords(updated);
                      }}
                      placeholder="mmHg, bpm, °C"
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Classificação</Label>
                    <Select
                      value={editedRecords[originalIndex]?.classification || ''}
                      onValueChange={(value) => {
                        const updated = [...editedRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          classification: value
                        };
                        setEditedRecords(updated);
                      }}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="elevado">Elevado</SelectItem>
                        <SelectItem value="baixo">Baixo</SelectItem>
                        <SelectItem value="alterado">Alterado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Horário</Label>
                    <Input
                      type="time"
                      value={editedRecords[originalIndex]?.time || ''}
                      onChange={(e) => {
                        const updated = [...editedRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          time: e.target.value
                        };
                        setEditedRecords(updated);
                      }}
                      className="h-8"
                    />
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button size="sm" onClick={() => handleSave(originalIndex)} disabled={isSaving}>
                    {isSaving ? "Salvando..." : "Salvar"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleCancel} disabled={isSaving}>
                    Cancelar
                  </Button>
                </div>
              </div>
            </td>
          </tr>
        ) : (
          <tr className="border-b border-border hover:bg-muted/30 transition-colors">
            <td className="p-3 text-sm text-muted-foreground">
              <div className="truncate">
                {record.date && !isNaN(new Date(record.date).getTime())
                  ? format(new Date(record.date), "dd/MM/yyyy")
                  : '–'}
              </div>
            </td>
            <td className="p-3">
              <div className="flex items-center gap-2">
                <Icon className="h-4 w-4 text-red-600" />
                <span className="text-sm font-medium truncate">
                  {vitalTypeLabels[record.vital_type?.toLowerCase()] || record.vital_type || '–'}
                </span>
              </div>
            </td>
            <td className="p-3">
              <div className="text-sm font-semibold truncate text-secondary">
                {record.value !== undefined && record.value !== null ? record.value : '–'}
              </div>
            </td>
            <td className="p-3">
              <div className="text-sm text-muted-foreground truncate">
                {record.unit || defaultUnits[record.vital_type?.toLowerCase()] || '–'}
              </div>
            </td>
            <td className="p-3">
              {record.classification && (
                <Badge 
                  variant={record.classification === 'normal' ? 'default' : 'destructive'} 
                  className="text-xs"
                >
                  {record.classification}
                </Badge>
              )}
            </td>
            <td className="p-3 text-right">
              <div className="flex gap-1 justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingIndex(originalIndex)}
                  className="h-7 px-2"
                >
                  <Pencil className="h-3 w-3" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                      <AlertDialogDescription>
                        Tem certeza que deseja excluir este sinal vital?
                        <br />
                        <span className="font-medium mt-2 block">
                          {record.date && !isNaN(new Date(record.date).getTime())
                            ? format(new Date(record.date), "dd/MM/yyyy")
                            : 'Data inválida'} - {vitalTypeLabels[record.vital_type?.toLowerCase()] || record.vital_type || 'Sinal Vital'}
                        </span>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleDelete(originalIndex)}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Excluir
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  // Função para renderizar card mobile
  const renderMobileCard = (record: EditableVitalSign, groupIndex: number, dateKey: string) => {
    const originalIndex = record._index ?? groupIndex;
    const isEditing = editingIndex === originalIndex;
    const Icon = vitalTypeIcons[record.vital_type?.toLowerCase()] || Heart;

    return (
      <Card key={`${dateKey}-${originalIndex}-mobile`} className="mb-3">
        <CardContent className="p-4">
          {isEditing ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Data</Label>
                  <Input
                    type="date"
                    value={editedRecords[originalIndex]?.date || ''}
                    onChange={(e) => {
                      const updated = [...editedRecords];
                      updated[originalIndex] = {
                        ...updated[originalIndex],
                        date: e.target.value
                      };
                      setEditedRecords(updated);
                    }}
                    className="h-8"
                  />
                </div>
                <div>
                  <Label className="text-xs">Tipo</Label>
                  <Select
                    value={editedRecords[originalIndex]?.vital_type || ''}
                    onValueChange={(value) => {
                      const updated = [...editedRecords];
                      updated[originalIndex] = {
                        ...updated[originalIndex],
                        vital_type: value,
                        unit: defaultUnits[value] || updated[originalIndex].unit
                      };
                      setEditedRecords(updated);
                    }}
                  >
                    <SelectTrigger className="h-8">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pressao_arterial">Pressão Arterial</SelectItem>
                      <SelectItem value="frequencia_cardiaca">Frequência Cardíaca</SelectItem>
                      <SelectItem value="temperatura">Temperatura</SelectItem>
                      <SelectItem value="frequencia_respiratoria">Frequência Respiratória</SelectItem>
                      <SelectItem value="saturacao_oxigenio">Saturação de Oxigênio</SelectItem>
                      <SelectItem value="glicemia">Glicemia</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Valor</Label>
                  <Input
                    value={editedRecords[originalIndex]?.value ?? ''}
                    onChange={(e) => {
                      const updated = [...editedRecords];
                      updated[originalIndex] = {
                        ...updated[originalIndex],
                        value: e.target.value
                      };
                      setEditedRecords(updated);
                    }}
                    placeholder="Ex: 120/80"
                    className="h-8"
                  />
                </div>
                <div>
                  <Label className="text-xs">Unidade</Label>
                  <Input
                    value={editedRecords[originalIndex]?.unit || ''}
                    onChange={(e) => {
                      const updated = [...editedRecords];
                      updated[originalIndex] = {
                        ...updated[originalIndex],
                        unit: e.target.value
                      };
                      setEditedRecords(updated);
                    }}
                    placeholder="mmHg, bpm"
                    className="h-8"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Classificação</Label>
                  <Select
                    value={editedRecords[originalIndex]?.classification || ''}
                    onValueChange={(value) => {
                      const updated = [...editedRecords];
                      updated[originalIndex] = {
                        ...updated[originalIndex],
                        classification: value
                      };
                      setEditedRecords(updated);
                    }}
                  >
                    <SelectTrigger className="h-8">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="elevado">Elevado</SelectItem>
                      <SelectItem value="baixo">Baixo</SelectItem>
                      <SelectItem value="alterado">Alterado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Horário</Label>
                  <Input
                    type="time"
                    value={editedRecords[originalIndex]?.time || ''}
                    onChange={(e) => {
                      const updated = [...editedRecords];
                      updated[originalIndex] = {
                        ...updated[originalIndex],
                        time: e.target.value
                      };
                      setEditedRecords(updated);
                    }}
                    className="h-8"
                  />
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button size="sm" onClick={() => handleSave(originalIndex)} disabled={isSaving} className="flex-1">
                  {isSaving ? "Salvando..." : "Salvar"}
                </Button>
                <Button size="sm" variant="outline" onClick={handleCancel} disabled={isSaving} className="flex-1">
                  Cancelar
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon className="h-5 w-5 text-red-600" />
                  <span className="font-medium">
                    {vitalTypeLabels[record.vital_type?.toLowerCase()] || record.vital_type || '–'}
                  </span>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingIndex(originalIndex)}
                    className="h-7 px-2"
                  >
                    <Pencil className="h-3 w-3" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                        <AlertDialogDescription>
                          Tem certeza que deseja excluir este sinal vital?
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDelete(originalIndex)}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Excluir
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold text-secondary">
                  {record.value !== undefined && record.value !== null ? record.value : '–'}
                </span>
                <span className="text-sm text-muted-foreground">
                  {record.unit || defaultUnits[record.vital_type?.toLowerCase()] || ''}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  {record.date && !isNaN(new Date(record.date).getTime())
                    ? format(new Date(record.date), "dd/MM/yyyy")
                    : '–'}
                  {record.time && ` às ${record.time}`}
                </span>
                {record.classification && (
                  <Badge 
                    variant={record.classification === 'normal' ? 'default' : 'destructive'} 
                    className="text-xs"
                  >
                    {record.classification}
                  </Badge>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-4 p-4 bg-background border-t">
      {isMobile ? (
        /* Mobile: Cards View */
        <div className="p-3">
          {sortedDates.map((dateKey) => {
            const group = groupedByDate[dateKey];
            const isExpanded = expandedDateGroups.get(dateKey) ?? expandAllByDefault;

            return (
              <div key={dateKey} className="mb-4">
                <Collapsible
                  open={isExpanded}
                  onOpenChange={(open) => toggleDateGroup(dateKey, open)}
                >
                  <CollapsibleTrigger asChild>
                    <div className="flex items-center gap-2 text-xs font-semibold text-red-600 cursor-pointer bg-red-500/5 hover:bg-red-500/10 rounded-lg px-3 py-2 mb-2 transition-colors">
                      {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      <Heart className="h-4 w-4" />
                      <span>📅 {group.displayKey}</span>
                      <span className="ml-auto font-normal text-muted-foreground mr-2">{isExpanded ? '- Ver menos' : '+ Ver mais'}</span>
                      <Badge variant="secondary">
                        {group.records.length} registro{group.records.length !== 1 ? 's' : ''}
                      </Badge>
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    {group.records.map((record, idx) =>
                      renderMobileCard(record, idx, dateKey)
                    )}
                  </CollapsibleContent>
                </Collapsible>
              </div>
            );
          })}
        </div>
      ) : (
        /* Desktop: Table View */
        <div className="overflow-x-auto">
          <table className="w-full table-fixed">
            <colgroup>
              <col style={{ width: '120px' }} />
              <col style={{ width: '200px' }} />
              <col style={{ width: '120px' }} />
              <col style={{ width: '80px' }} />
              <col style={{ width: '100px' }} />
              <col style={{ width: '120px' }} />
            </colgroup>
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Data
                </th>
                <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Tipo
                </th>
                <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Valor
                </th>
                <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Unidade
                </th>
                <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Status
                </th>
                <th className="p-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedDates.map((dateKey) => {
                const group = groupedByDate[dateKey];
                const isExpanded = expandedDateGroups.get(dateKey) ?? expandAllByDefault;

                return (
                  <React.Fragment key={dateKey}>
                    <tr className="bg-red-500/5 border-y-2 border-red-500/20">
                      <td colSpan={6} className="p-2">
                        <Collapsible open={isExpanded} onOpenChange={(open) => toggleDateGroup(dateKey, open)}>
                          <CollapsibleTrigger asChild>
                            <div className="flex items-center gap-2 text-xs font-semibold text-red-600 cursor-pointer hover:bg-red-500/10 rounded px-2 py-1 transition-colors w-full">
                              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                              <Heart className="h-4 w-4" />
                              <span>📅 {group.displayKey}</span>
                              <span className="ml-auto font-normal text-muted-foreground mr-2">{isExpanded ? '- Ver menos' : '+ Ver mais'}</span>
                              <Badge variant="secondary">
                                {group.records.length} registro{group.records.length !== 1 ? 's' : ''}
                              </Badge>
                            </div>
                          </CollapsibleTrigger>
                        </Collapsible>
                      </td>
                    </tr>

                    {isExpanded && (
                      <>
                        {group.records.map((record, idx) => renderRecordRow(record, idx, dateKey))}
                      </>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="p-3 text-xs text-muted-foreground border-t border-border bg-muted/30">
        Total: {vitalsRecords.length} registro(s) de sinais vitais em {sortedDates.length} mês(es)/ano(s)
      </div>
    </div>
  );
}

